import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { rgbaEquals, toCssHexa, type Rgba } from '../../sim/color.ts';
import {
  MAX_ELEVATION,
  MAX_SKIRT_M,
  MAX_WATER_DEPTH,
  MIN_ELEVATION,
  MIN_SKIRT_M,
  MIN_WATER_DEPTH,
  MIN_TERRACE_LEVELS,
  MAX_TERRACE_LEVELS,
  MAX_TERRACE_RADIUS,
  PROPS,
  formatRouteMetres,
  gateWidth,
  routeLengthM,
  selectedCheckpointPoles,
  selectedEdgeIndex,
  soleRef,
  type EditorTool,
  type MapDocument,
  type ObjectKind,
  type ObjectRef,
  type Selection,
  type PropId,
  type TerraceKind,
  type WaterMaterial,
} from '../../sim/map/index.ts';
import { openColorPicker } from '../colorPicker.tsx';
import { ScrubNumber } from '../scrubNumber.tsx';
import { Tooltip } from '../tooltip.tsx';
import {
  CopyPlusIcon,
  EditorIcon,
  StraightenIcon,
  TrashIcon,
} from './icons.tsx';

const MATERIALS: ReadonlyArray<{ id: WaterMaterial; label: string }> = [
  { id: 'sand', label: 'Sand' },
  { id: 'stone', label: 'Stone' },
  { id: 'reef', label: 'Reef' },
];

const TERRACE_KINDS: ReadonlyArray<{ id: TerraceKind; label: string }> = [
  { id: 'island', label: 'Island' },
  { id: 'rock', label: 'Rock' },
];

/** Keep in sync with `--editor-context-slide` in `toolbar.css`. */
const CONTEXT_SLIDE_MS = 180;

function stripHasContent(tool: EditorTool, selection: Selection): boolean {
  return selection.refs.length > 0 || tool === 'draw-water' || tool === 'draw-terrace';
}

function slideMs(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : CONTEXT_SLIDE_MS;
}

const KIND_LABELS: Record<ObjectKind, { one: string; many: string }> = {
  island: { one: 'Island', many: 'Islands' },
  rock: { one: 'Rock', many: 'Rocks' },
  water: { one: 'Shallow water', many: 'Shallow water zones' },
  startArea: { one: 'Start area', many: 'Start areas' },
  parkArea: { one: 'Park area', many: 'Park areas' },
  checkpoint: { one: 'Checkpoint', many: 'Checkpoints' },
  route: { one: 'Route', many: 'Routes' },
};

const RING_KINDS: ReadonlySet<ObjectKind> = new Set([
  'island',
  'rock',
  'water',
  'startArea',
  'parkArea',
]);

type Shared<T> = { mixed: false; value: T } | { mixed: true };

export type EditorContextActions = {
  getMap: () => MapDocument;
  setDraftMaterial: (material: WaterMaterial) => void;
  setDraftDepth: (depth: number) => void;
  setDraftProp: (prop: PropId) => void;
  setDraftTerraceKind: (kind: TerraceKind) => void;
  setDraftTerraceLevels: (levels: number) => void;
  setDraftTerracePeakM: (peakM: number) => void;
  setDraftTerraceRadius: (radius: number) => void;
  setDraftTerraceColor: (color: Rgba) => void;
  setDraftTerraceColorVariation: (variation: number) => void;
  setDraftTerraceJitter: (jitter: number) => void;
  setDraftTerraceElevRand: (elevRand: number) => void;
  setDraftTerraceRadiusRand: (radiusRand: number) => void;
  onSetCornerRadius: (radius: number) => void;
  onStraightenEdge: () => void;
  onSetMaterial: (material: WaterMaterial) => void;
  onColorLive: (color: Rgba) => void;
  onColorCommit: () => void;
  onColorCancel: () => void;
  onSetElevation: (elevation: number) => void;
  onSetSkirtM: (skirtM: number) => void;
  onScatterProps: () => void;
  onClearProps: () => void;
  onSetDepth: (depth: number) => void;
  onSetCheckpointOrder: (order: number | null) => void;
  onSetCheckpointGoal: (isGoal: boolean) => void;
  onSetGateWidth: (width: number) => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

function sharedValue<T>(values: readonly T[]): Shared<T> | null {
  const first = values[0];

  if (first === undefined) {
    return null;
  }

  return values.every((value) => value === first) ? { mixed: false, value: first } : { mixed: true };
}

function sharedRgba(values: readonly Rgba[]): { mixed: boolean; value: Rgba } | null {
  const first = values[0];

  if (!first) {
    return null;
  }

  return {
    mixed: values.some((value) => !rgbaEquals(value, first)),
    value: first,
  };
}

function selectionTitle(refs: readonly ObjectRef[]): string {
  const kinds = [...new Set(refs.map((ref) => ref.kind))];
  const only = kinds.length === 1 ? kinds[0] : null;

  if (!only) {
    return 'Selection';
  }

  const label = KIND_LABELS[only];
  return refs.length === 1 ? label.one : label.many;
}

function collectRings(
  map: MapDocument,
  refs: readonly ObjectRef[],
): Array<{ cornerRadius: number; color: Rgba }> {
  const rings: Array<{ cornerRadius: number; color: Rgba }> = [];

  for (const ref of refs) {
    if (ref.kind === 'island') {
      const island = map.islands.find((item) => item.id === ref.id);
      if (island) rings.push(island);
    } else if (ref.kind === 'rock') {
      const rock = map.rocks.find((item) => item.id === ref.id);
      if (rock) rings.push(rock);
    } else if (ref.kind === 'water') {
      const zone = map.shallowWater.find((item) => item.id === ref.id);
      if (zone) rings.push(zone);
    } else if (ref.kind === 'startArea' && map.startArea) {
      rings.push(map.startArea);
    } else if (ref.kind === 'parkArea' && map.parkArea) {
      rings.push(map.parkArea);
    }
  }

  return rings;
}

function collectElevations(map: MapDocument, refs: readonly ObjectRef[]): number[] {
  const values: number[] = [];

  for (const ref of refs) {
    if (ref.kind === 'island') {
      const island = map.islands.find((item) => item.id === ref.id);
      if (island) values.push(island.elevation);
    } else if (ref.kind === 'rock') {
      const rock = map.rocks.find((item) => item.id === ref.id);
      if (rock) values.push(rock.elevation);
    }
  }

  return values;
}

function collectSkirts(map: MapDocument, refs: readonly ObjectRef[]): number[] {
  const values: number[] = [];

  for (const ref of refs) {
    if (ref.kind === 'island') {
      const island = map.islands.find((item) => item.id === ref.id);
      if (island) values.push(island.skirtM);
    }
  }

  return values;
}

function collectDepths(map: MapDocument, refs: readonly ObjectRef[]): number[] {
  const values: number[] = [];

  for (const ref of refs) {
    if (ref.kind === 'water') {
      const zone = map.shallowWater.find((item) => item.id === ref.id);
      if (zone) values.push(zone.depth);
    }
  }

  return values;
}

function collectCheckpoints(map: MapDocument, refs: readonly ObjectRef[]) {
  return refs
    .filter((ref) => ref.kind === 'checkpoint')
    .map((ref) => map.checkpoints.find((item) => item.id === (ref as { id: string }).id))
    .filter((checkpoint): checkpoint is NonNullable<typeof checkpoint> => checkpoint !== undefined);
}

function collectRoutes(map: MapDocument, refs: readonly ObjectRef[]) {
  return refs
    .filter((ref) => ref.kind === 'route')
    .map((ref) => map.routes.find((item) => item.id === (ref as { id: string }).id))
    .filter((route): route is NonNullable<typeof route> => route !== undefined);
}

function StripIconButton({
  label,
  shortcut,
  onClick,
  icon,
}: {
  label: string;
  shortcut?: string;
  onClick: () => void;
  icon: typeof CopyPlusIcon;
}): JSX.Element {
  return (
    <Tooltip label={label} shortcut={shortcut}>
      <button type="button" class="editor-rail-button editor-options-icon" aria-label={label} onClick={onClick}>
        <EditorIcon icon={icon} />
      </button>
    </Tooltip>
  );
}

function StripActions({
  onDuplicate,
  onDelete,
}: {
  onDuplicate: () => void;
  onDelete: () => void;
}): JSX.Element {
  return (
    <>
      <div class="editor-options-divider" role="separator" aria-orientation="vertical" />
      <div class="editor-options-actions">
        <StripIconButton label="Duplicate" shortcut="Ctrl+D" icon={CopyPlusIcon} onClick={onDuplicate} />
        <StripIconButton label="Delete" shortcut="Del" icon={TrashIcon} onClick={onDelete} />
      </div>
    </>
  );
}

function StripScrub({
  label,
  tooltip,
  shared,
  min,
  max,
  step,
  allowEmpty = false,
  onClear,
  onChange,
}: {
  label: string;
  tooltip: string;
  shared: Shared<number | null>;
  min?: number;
  max?: number;
  step?: number;
  allowEmpty?: boolean;
  onClear?: () => void;
  onChange: (next: number) => void;
}): JSX.Element {
  const value = shared.mixed || shared.value === null ? undefined : shared.value;

  return (
    <Tooltip label={tooltip}>
      <ScrubNumber
        label={label}
        mixed={shared.mixed}
        value={value}
        min={min}
        max={max}
        step={step}
        allowEmpty={allowEmpty}
        onClear={onClear}
        onChange={onChange}
      />
    </Tooltip>
  );
}

function StripSelect<T extends string>({
  label,
  tooltip,
  shared,
  options,
  onChange,
}: {
  label: string;
  tooltip: string;
  shared: Shared<T>;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <Tooltip label={tooltip}>
      <label class="editor-options-field">
        <span class="editor-options-label">{label}</span>
        {shared.mixed ? (
          <select
            class="editor-options-select"
            aria-label={label}
            value=""
            onChange={(event) => {
              if (event.currentTarget.value !== '') {
                onChange(event.currentTarget.value as T);
              }
            }}
          >
            <option value="">—</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <select
            class="editor-options-select"
            aria-label={label}
            value={shared.value}
            onChange={(event) => onChange(event.currentTarget.value as T)}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </label>
    </Tooltip>
  );
}

function StripGoal({
  shared,
  onChange,
}: {
  shared: Shared<boolean>;
  onChange: (next: boolean) => void;
}): JSX.Element {
  const value = shared.mixed ? false : shared.value;

  return (
    <Tooltip label="Lap line. The last crossing finishes the race.">
      <button
        type="button"
        class="ui-button editor-options-goal"
        aria-label="Goal"
        aria-pressed={value ? 'true' : 'false'}
        onClick={() => onChange(!value)}
      >
        Goal
      </button>
    </Tooltip>
  );
}

function StripColorSwatch({
  value,
  mixed,
  onChange,
  onCommit,
  onCancel,
}: {
  value: Rgba;
  mixed: boolean;
  onChange: (next: Rgba) => void;
  onCommit?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  return (
    <Tooltip label="Colour">
      <button
        type="button"
        class={`ui-color-swatch ui-color-check editor-options-swatch${mixed ? ' is-mixed' : ''}`}
        aria-label="Colour"
        aria-haspopup="dialog"
        data-color={toCssHexa(value)}
        onClick={(event) =>
          openColorPicker({
            label: 'Colour',
            value,
            onChange,
            onCommit,
            onCancel,
            anchor: event.currentTarget.getBoundingClientRect(),
          })
        }
      >
        {mixed ? (
          <span class="ui-color-swatch-fill" />
        ) : (
          <span class="ui-color-swatch-fill" style={{ background: toCssHexa(value) }} />
        )}
      </button>
    </Tooltip>
  );
}

function SelectionContext({
  selection,
  draftProp,
  canPlaceProps,
  actions,
}: {
  selection: Selection;
  draftProp: PropId;
  canPlaceProps: boolean;
  actions: EditorContextActions;
}): JSX.Element {
  const map = actions.getMap();
  const refs = selection.refs;
  const sole = soleRef(selection);

  if (sole && RING_KINDS.has(sole.kind) && selectedEdgeIndex(selection, sole) !== null) {
    return (
      <>
        <span class="editor-options-title">Bend point</span>
        <StripIconButton label="Straighten edge" icon={StraightenIcon} onClick={actions.onStraightenEdge} />
        <StripActions onDuplicate={actions.onDuplicate} onDelete={actions.onDelete} />
      </>
    );
  }

  const rings = collectRings(map, refs);
  const checkpoints = collectCheckpoints(map, refs);
  const routes = collectRoutes(map, refs);

  const cornerRadius = sharedValue(rings.map((ring) => ring.cornerRadius));
  const material = sharedValue(
    refs
      .filter((ref) => ref.kind === 'water')
      .map((ref) => map.shallowWater.find((zone) => zone.id === (ref as { id: string }).id)?.material)
      .filter((value): value is WaterMaterial => value !== undefined),
  );
  const color = sharedRgba([...rings.map((ring) => ring.color), ...routes.map((route) => route.color)]);
  const routeLength = sharedValue(
    routes.map((route) => Math.round(routeLengthM(route.points, route.cornerRadius))),
  );
  const order = sharedValue(checkpoints.map((checkpoint) => checkpoint.order));
  const isGoal = sharedValue(checkpoints.map((checkpoint) => checkpoint.isGoal));
  const width = sharedValue(checkpoints.map((checkpoint) => gateWidth(checkpoint)));
  const elevation = sharedValue(collectElevations(map, refs));
  const islandSelected = refs.some((ref) => ref.kind === 'island');
  const skirt = sharedValue(collectSkirts(map, refs));
  const depth = sharedValue(collectDepths(map, refs));

  const poles =
    sole?.kind === 'checkpoint' ? selectedCheckpointPoles(selection, sole.id).join(',') : undefined;

  return (
    <>
      <span class="editor-options-title" data-poles={poles}>
        {selectionTitle(refs)}
        {refs.length > 1 ? <span class="editor-options-count">{refs.length}</span> : null}
      </span>
      {cornerRadius ? (
        <StripScrub
          label="Radius"
          tooltip="Fillet at ring corners (m)"
          shared={cornerRadius}
          min={0}
          max={500}
          step={5}
          onChange={actions.onSetCornerRadius}
        />
      ) : null}
      {material ? (
        <StripSelect<WaterMaterial>
          label="Material"
          tooltip="Shallow-water material"
          shared={material}
          options={[
            { value: 'sand', label: 'Sand' },
            { value: 'stone', label: 'Stone' },
            { value: 'reef', label: 'Reef' },
          ]}
          onChange={actions.onSetMaterial}
        />
      ) : null}
      {order ? (
        <StripScrub
          label="Order"
          tooltip="Blank = any order, each required. Same number = pick one."
          shared={order}
          allowEmpty
          min={0}
          step={1}
          onClear={() => actions.onSetCheckpointOrder(null)}
          onChange={(value) => actions.onSetCheckpointOrder(value <= 0 ? null : Math.round(value))}
        />
      ) : null}
      {isGoal ? <StripGoal shared={isGoal} onChange={actions.onSetCheckpointGoal} /> : null}
      {width ? (
        <StripScrub
          label="Width"
          tooltip="Checkpoint gate width (m)"
          shared={width}
          min={10}
          max={2000}
          step={10}
          onChange={actions.onSetGateWidth}
        />
      ) : null}
      {routeLength ? (
        <label class="editor-options-field">
          <span class="editor-options-label">Length</span>
          <span>{routeLength.mixed ? 'mixed' : formatRouteMetres(routeLength.value)}</span>
        </label>
      ) : null}
      {color ? (
        <StripColorSwatch
          value={color.value}
          mixed={color.mixed}
          onChange={actions.onColorLive}
          onCommit={actions.onColorCommit}
          onCancel={actions.onColorCancel}
        />
      ) : null}
      {elevation ? (
        <StripScrub
          label="Elevation"
          tooltip="How far this sticks up from the water, or from the land it sits on (m). Shade starts at the shoreline."
          shared={elevation}
          min={MIN_ELEVATION}
          max={MAX_ELEVATION}
          step={1}
          onChange={actions.onSetElevation}
        />
      ) : null}
      {skirt ? (
        <StripScrub
          label="Skirt"
          tooltip="Sand lip at the waterline (m)"
          shared={skirt}
          min={MIN_SKIRT_M}
          max={MAX_SKIRT_M}
          step={1}
          onChange={actions.onSetSkirtM}
        />
      ) : null}
      {islandSelected ? (
        <StripSelect<PropId>
          label="Prop"
          tooltip="Prop planted by Place prop"
          shared={{ mixed: false, value: draftProp }}
          options={PROPS.map((entry) => ({ value: entry.id, label: entry.label }))}
          onChange={actions.setDraftProp}
        />
      ) : null}
      {islandSelected ? (
        <>
          <Tooltip
            label={
              canPlaceProps
                ? 'Scatter the selected prop across the island'
                : 'Scatter — turn Elevation off'
            }
          >
            <button
              type="button"
              class="ui-button"
              disabled={!canPlaceProps}
              onClick={actions.onScatterProps}
            >
              Scatter
            </button>
          </Tooltip>
          <Tooltip label="Remove every prop on the selected islands">
            <button type="button" class="ui-button" onClick={actions.onClearProps}>
              Clear props
            </button>
          </Tooltip>
        </>
      ) : null}
      {depth ? (
        <StripScrub
          label="Depth"
          tooltip="Depth below the surface (m). Deeper beds fade into the seafloor."
          shared={depth}
          min={MIN_WATER_DEPTH}
          max={MAX_WATER_DEPTH}
          step={1}
          onChange={actions.onSetDepth}
        />
      ) : null}
      <StripActions onDuplicate={actions.onDuplicate} onDelete={actions.onDelete} />
    </>
  );
}

function TerraceDraftContext({
  kind,
  levels,
  peakM,
  radius,
  color,
  colorVariation,
  jitter,
  elevRand,
  radiusRand,
  actions,
}: {
  kind: TerraceKind;
  levels: number;
  peakM: number;
  radius: number;
  color: Rgba;
  colorVariation: number;
  jitter: number;
  elevRand: number;
  radiusRand: number;
  actions: EditorContextActions;
}): JSX.Element {
  return (
    <div class="editor-options-tools">
      {TERRACE_KINDS.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          class="ui-button"
          data-terrace-kind={id}
          aria-pressed={kind === id ? 'true' : 'false'}
          onClick={() => actions.setDraftTerraceKind(id)}
        >
          {label}
        </button>
      ))}
      <StripColorSwatch value={color} mixed={false} onChange={actions.setDraftTerraceColor} />
      <StripScrub
        label="Color Variation"
        tooltip="How much lighter the peak is than the shore. 0 keeps every ring the same colour."
        shared={{ mixed: false, value: colorVariation }}
        min={0}
        max={1}
        step={0.05}
        onChange={actions.setDraftTerraceColorVariation}
      />
      <StripScrub
        label="Levels"
        tooltip="How many nested rings the next close will emit."
        shared={{ mixed: false, value: levels }}
        min={MIN_TERRACE_LEVELS}
        max={MAX_TERRACE_LEVELS}
        step={1}
        onChange={actions.setDraftTerraceLevels}
      />
      <StripScrub
        label="Peak"
        tooltip="World height of the top terrace (m). Risers sum to this number."
        shared={{ mixed: false, value: peakM }}
        min={MIN_ELEVATION}
        max={MAX_ELEVATION}
        step={1}
        onChange={actions.setDraftTerracePeakM}
      />
      <StripScrub
        label="Radius"
        tooltip="Fillet at the shore corners. Inner terraces scale down from this."
        shared={{ mixed: false, value: radius }}
        min={0}
        max={MAX_TERRACE_RADIUS}
        step={5}
        onChange={actions.setDraftTerraceRadius}
      />
      <StripScrub
        label="Jitter"
        tooltip="0 is concentric. Higher values nudge inner shores so they do not fill the shape."
        shared={{ mixed: false, value: jitter }}
        min={0}
        max={1}
        step={0.05}
        onChange={actions.setDraftTerraceJitter}
      />
      <StripScrub
        label="Elev. rand"
        tooltip="Varies each terrace’s riser. 0 is even; the risers still sum to Peak."
        shared={{ mixed: false, value: elevRand }}
        min={0}
        max={1}
        step={0.05}
        onChange={actions.setDraftTerraceElevRand}
      />
      <StripScrub
        label="Radius rand"
        tooltip="Jitters inner fillets only. The shore radius stays what you set."
        shared={{ mixed: false, value: radiusRand }}
        min={0}
        max={1}
        step={0.05}
        onChange={actions.setDraftTerraceRadiusRand}
      />
    </div>
  );
}

function ContextStripBody({
  tool,
  draftMaterial,
  draftDepth,
  draftProp,
  draftTerraceKind,
  draftTerraceLevels,
  draftTerracePeakM,
  draftTerraceRadius,
  draftTerraceColor,
  draftTerraceColorVariation,
  draftTerraceJitter,
  draftTerraceElevRand,
  draftTerraceRadiusRand,
  selection,
  canPlaceProps,
  actions,
}: {
  tool: EditorTool;
  draftMaterial: WaterMaterial;
  draftDepth: number;
  draftProp: PropId;
  draftTerraceKind: TerraceKind;
  draftTerraceLevels: number;
  draftTerracePeakM: number;
  draftTerraceRadius: number;
  draftTerraceColor: Rgba;
  draftTerraceColorVariation: number;
  draftTerraceJitter: number;
  draftTerraceElevRand: number;
  draftTerraceRadiusRand: number;
  selection: Selection;
  canPlaceProps: boolean;
  actions: EditorContextActions;
}): JSX.Element | null {
  if (selection.refs.length > 0) {
    return (
      <SelectionContext
        selection={selection}
        draftProp={draftProp}
        canPlaceProps={canPlaceProps}
        actions={actions}
      />
    );
  }

  if (tool === 'draw-water') {
    return (
      <div class="editor-options-tools">
        {MATERIALS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            class="ui-button"
            data-material={id}
            aria-pressed={draftMaterial === id ? 'true' : 'false'}
            onClick={() => actions.setDraftMaterial(id)}
          >
            {label}
          </button>
        ))}
        <StripScrub
          label="Depth"
          tooltip="Depth below the surface (m). Deeper beds fade into the seafloor."
          shared={{ mixed: false, value: draftDepth }}
          min={MIN_WATER_DEPTH}
          max={MAX_WATER_DEPTH}
          step={1}
          onChange={actions.setDraftDepth}
        />
      </div>
    );
  }

  if (tool === 'draw-terrace') {
    return (
      <TerraceDraftContext
        kind={draftTerraceKind}
        levels={draftTerraceLevels}
        peakM={draftTerracePeakM}
        radius={draftTerraceRadius}
        color={draftTerraceColor}
        colorVariation={draftTerraceColorVariation}
        jitter={draftTerraceJitter}
        elevRand={draftTerraceElevRand}
        radiusRand={draftTerraceRadiusRand}
        actions={actions}
      />
    );
  }

  return null;
}

export function ContextStrip({
  tool,
  draftMaterial,
  draftDepth,
  draftProp,
  draftTerraceKind,
  draftTerraceLevels,
  draftTerracePeakM,
  draftTerraceRadius,
  draftTerraceColor,
  draftTerraceColorVariation,
  draftTerraceJitter,
  draftTerraceElevRand,
  draftTerraceRadiusRand,
  selection,
  canPlaceProps,
  actions,
}: {
  tool: EditorTool;
  draftMaterial: WaterMaterial;
  draftDepth: number;
  draftProp: PropId;
  draftTerraceKind: TerraceKind;
  draftTerraceLevels: number;
  draftTerracePeakM: number;
  draftTerraceRadius: number;
  draftTerraceColor: Rgba;
  draftTerraceColorVariation: number;
  draftTerraceJitter: number;
  draftTerraceElevRand: number;
  draftTerraceRadiusRand: number;
  selection: Selection;
  canPlaceProps: boolean;
  actions: EditorContextActions;
}): JSX.Element {
  const live = {
    tool,
    draftMaterial,
    draftDepth,
    draftProp,
    draftTerraceKind,
    draftTerraceLevels,
    draftTerracePeakM,
    draftTerraceRadius,
    draftTerraceColor,
    draftTerraceColorVariation,
    draftTerraceJitter,
    draftTerraceElevRand,
    draftTerraceRadiusRand,
    selection,
  };
  const hasContent = stripHasContent(tool, selection);
  const [open, setOpen] = useState(hasContent);
  const [exit, setExit] = useState(live);
  const shown = hasContent ? live : exit;

  useEffect(() => {
    if (hasContent) {
      setExit(live);
      const frame = requestAnimationFrame(() => setOpen(true));
      return () => cancelAnimationFrame(frame);
    }

    setOpen(false);
    const timer = window.setTimeout(() => {
      setExit(live);
    }, slideMs());
    return () => window.clearTimeout(timer);
  }, [
    hasContent,
    tool,
    draftMaterial,
    draftDepth,
    draftProp,
    draftTerraceKind,
    draftTerraceLevels,
    draftTerracePeakM,
    draftTerraceRadius,
    draftTerraceColor,
    draftTerraceColorVariation,
    draftTerraceJitter,
    draftTerraceElevRand,
    draftTerraceRadiusRand,
    selection,
  ]);

  return (
    <div
      class={`editor-options ui-panel ui-chrome${open ? ' is-open' : ''}`}
      role="toolbar"
      aria-label="Context"
      aria-hidden={open ? 'false' : 'true'}
      inert={!open}
    >
      <div class="editor-options-scroll">
        <ContextStripBody
          tool={shown.tool}
          draftMaterial={shown.draftMaterial}
          draftDepth={shown.draftDepth}
          draftProp={shown.draftProp}
          draftTerraceKind={shown.draftTerraceKind}
          draftTerraceLevels={shown.draftTerraceLevels}
          draftTerracePeakM={shown.draftTerracePeakM}
          draftTerraceRadius={shown.draftTerraceRadius}
          draftTerraceColor={shown.draftTerraceColor}
          draftTerraceColorVariation={shown.draftTerraceColorVariation}
          draftTerraceJitter={shown.draftTerraceJitter}
          draftTerraceElevRand={shown.draftTerraceElevRand}
          draftTerraceRadiusRand={shown.draftTerraceRadiusRand}
          selection={shown.selection}
          canPlaceProps={canPlaceProps}
          actions={actions}
        />
      </div>
    </div>
  );
}
